import { useEffect } from "react";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import BeforeAfterGallery, { TRANSFORMATIONS } from "@/components/shared/BeforeAfterGallery";
import RealResults from "@/components/landing/RealResults";
import { Quote } from "lucide-react";
import SEO from "@/components/shared/SEO";

const stats = [
  { v: "27", u: "lbs", k: "avg. weight lost (24 wks)" },
  { v: "38", u: "%", k: "energy increase on HRT" },
  { v: "92", u: "%", k: "see hair regrowth by month 6" },
  { v: "4.9", u: "/5", k: "patient care experience" },
];

const stories = [
  {
    slug: "maya",
    name: "Maya · 38",
    side: "Women's Shop",
    text: "I tried six things. BestRx is the first that actually felt like medicine — and like someone was listening. Down 22 lbs, sleeping through the night, and finally have my brain back.",
    metric: "−22 LBS · 4 months",
    accent: "pink",
  },
  {
    slug: "andre",
    name: "Andre · 42",
    side: "Men's Garage",
    text: "Bloodwork, a real doctor on a Tuesday night, and a plan that didn't try to sell me everything. That's it. T restored, lean mass up 18 lbs.",
    metric: "TRT · +18 LB LEAN MASS",
    accent: "blue",
  },
  {
    slug: "lena",
    name: "Lena · 32",
    side: "Women's Shop",
    text: "Hormones were a mess after my second kid. Three months on the Balance program and I feel like myself for the first time in two years.",
    metric: "HRT · 3 MONTHS",
    accent: "pink",
  },
];

export default function ResultsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="bg-[var(--leen-bg)] text-white overflow-x-hidden min-h-screen">
      <SEO
        title="Real Patient Results & Transformations"
        path="/results"
        description="Real BestRx patient transformations: GLP-1 weight loss, testosterone restoration, hormone balance, hair regrowth. Bloodwork-driven outcomes with clinical oversight."
      />
      <Nav />

      {/* Real People Real Results (new — top of page) */}
      <RealResults />

      {/* HERO */}
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-20 overflow-hidden">
        <div className="absolute -top-20 -left-40 w-[480px] h-[480px] rounded-full blur-3xl bg-[var(--leen-pink)]/20" />
        <div className="absolute -bottom-32 right-0 w-[520px] h-[520px] rounded-full blur-3xl bg-[var(--leen-blue)]/20" />
        <div className="relative px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <p className="font-script text-[var(--leen-pink)] text-[68px] md:text-[100px] lg:text-[120px] leading-none italic -mb-4 md:-mb-6">
            Real
          </p>
          <h1 className="font-display text-[60px] sm:text-[100px] md:text-[140px] lg:text-[180px] leading-[0.85] tracking-tight">
            <span className="text-[var(--leen-pink)]">REAL</span> PEOPLE.<br />
            <span className="text-[var(--leen-blue)]">REAL</span> RESULTS.
          </h1>
          <p className="mt-7 font-body italic text-[18px] md:text-[22px] text-[var(--leen-ink-soft)] max-w-2xl">
            Composite outcomes from BestRx members across the last 12 months. Receipts vary.
            But the receipts exist.
          </p>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[var(--leen-bg)] py-8 md:py-12 border-y border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 py-6">
          {stats.map((s, i) => (
            <div key={i} data-testid={`results-stat-${i}`} className="flex flex-col">
              <div className="flex items-baseline gap-1">
                <span className="font-display text-[56px] md:text-[80px] lg:text-[100px] leading-none text-white">{s.v}</span>
                <span className="font-display text-[20px] md:text-[26px] text-[var(--leen-pink)]">{s.u}</span>
              </div>
              <p className="eyebrow text-[var(--leen-ink-soft)] mt-3">{s.k}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BEFORE & AFTER GALLERY */}
      <section className="bg-[var(--leen-bg)] py-20 md:py-28">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <p className="font-script text-[var(--leen-pink)] text-[44px] md:text-[64px] leading-none italic -mb-2">
                The
              </p>
              <h2 className="font-display text-[36px] sm:text-[52px] md:text-[64px] leading-none text-white">
                BEFORE · <span className="text-[var(--leen-pink)]">AFTER</span> GALLERY
              </h2>
            </div>
            <p className="font-body italic text-[15px] text-[var(--leen-ink-soft)] max-w-md">
              Eight real members. Eight real timelines. Each tile is one body changing in real time.
            </p>
          </div>
          <BeforeAfterGallery items={TRANSFORMATIONS} />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[var(--leen-card)] py-20 md:py-28 border-y border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="text-center mb-14">
            <p className="font-script text-[var(--leen-pink)] text-[44px] md:text-[60px] leading-none italic -mb-2">
              Their
            </p>
            <h2 className="font-display text-[36px] sm:text-[52px] md:text-[64px] leading-none text-white">
              <span className="text-[var(--leen-pink)]">SUCCESS</span> STORIES
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {stories.map((s) => {
              const accent = s.accent === "blue" ? "var(--leen-blue)" : "var(--leen-pink)";
              return (
                <figure
                  key={s.slug}
                  data-testid={`results-story-${s.slug}`}
                  className="bg-[var(--leen-bg)] border border-[var(--leen-line)] rounded-xl p-7 md:p-8 flex flex-col"
                >
                  <Quote size={28} style={{ color: accent }} className="mb-5" />
                  <blockquote className="font-display text-[18px] md:text-[22px] leading-[1.3] text-white flex-1">
                    {s.text}
                  </blockquote>
                  <figcaption className="mt-7 pt-5 border-t border-[var(--leen-line)] flex items-center justify-between gap-4">
                    <div>
                      <p className="eyebrow text-white">{s.name}</p>
                      <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--leen-ink-soft)] mt-1">{s.side}</p>
                    </div>
                    <span className="eyebrow" style={{ color: accent }}>{s.metric}</span>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </main>
  );
}
